// API helpers for thread + conversation reads.
//
// Originally written for the iOS WebView embed routes, now also consumed by
// the desktop Threads tab (`/threads`, `/threads/:id`). The same helpers
// work for both surfaces because `Api` (axios) attaches whichever token is
// present — embed-injected `window.__ETHOSPHERE_TOKEN__` for the WebView,
// Keycloak's `currentToken` for the regular browser flow.
//
// Endpoints (PRD §8.2 / §8.3):
//   GET /threads                            — paginated list (Threads tab)
//   GET /threads/{id}                       — thread + conversations + status
//   GET /threads/{id}/analysis-runs         — latest committed analysis run
//   GET /conversations/{id}                 — conversation metadata (thread_id)
//   GET /conversations/{id}/details         — eagerly-loaded snippet refs
//   GET /conversations/{id}/detail_items    — CLEANED_TRANSCRIPT items
//
// The SSE URL helper lives here too so tests + the live-transcript view
// agree on the exact path.

import { Api, getCurrentToken } from "../Api";
import { config } from "../runtimeConfig";

/**
 * GET /threads?archived=false&limit=50[&cursor=...]
 *
 * Paginated list of the current user's threads (newest-active-first per
 * PRD §8.2). Each row is a `ThreadListItem` — includes the enrichments
 * `status`, `recording_count`, `last_recorded_at`, `last_analyzed_at`.
 *
 * The desktop Threads tab calls this (no cursor, fixed limit=50 for v1).
 */
export async function listThreads({
  archived = "false",
  limit = 50,
  cursor = null,
} = {}) {
  const params = new URLSearchParams({
    archived: String(archived),
    limit: String(limit),
  });
  if (cursor) params.set("cursor", cursor);
  const response = await Api.get(`/threads?${params.toString()}`);
  return response.data;
}

/**
 * GET /threads/{threadId}
 *
 * Returns the thread row plus enrichments (conversations, last_analyzed_at,
 * derived status — see PRD §8.2).
 */
export async function getThread(threadId) {
  const response = await Api.get(`/threads/${threadId}`);
  return response.data;
}

/**
 * GET /threads/{threadId}/analysis-runs?status=committed&limit=1
 *
 * Returns the most recently COMMITTED analysis run (one row, possibly the
 * empty list if none have been produced yet). Used by the analysis embed
 * to render PRD §15.5's "Analysis + Summary" panel.
 */
export async function getLatestCommittedAnalysisRun(threadId, workflowKey) {
  const params = new URLSearchParams({
    status: "committed",
    limit: "1",
  });
  if (workflowKey) params.set("workflow_key", workflowKey);
  const response = await Api.get(
    `/threads/${threadId}/analysis-runs?${params.toString()}`
  );
  return response.data;
}

/**
 * GET /threads/{threadId}/analysis-runs?limit=N
 *
 * Lists analysis runs in any status (queued / started / committed / failed),
 * newest-first. Used by the Track-C Analysis section to detect an
 * in-flight manual run (latest is queued/running) and surface its
 * input-snapshot panel.
 */
export async function listAnalysisRuns(threadId, { limit = 5 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  const response = await Api.get(
    `/threads/${threadId}/analysis-runs?${params.toString()}`,
  );
  return response.data;
}

/**
 * POST /threads/{threadId}/analyze
 *
 * Manual re-analyze trigger (Track C). Backend pre-creates a queued
 * ThreadAnalysisRun row with an input snapshot, then starts
 * ThreadAnalysisWorkflow against it. The 30s/thread rate-limit returns
 * 429 + Retry-After when violated — callers should surface that to the
 * user via a toast instead of treating it as a generic error.
 *
 * Returns ``{run_id, workflow_id, snapshot_summary}`` (HTTP 202).
 */
export async function triggerManualAnalysis(threadId) {
  const response = await Api.post(`/threads/${threadId}/analyze`, {});
  return response.data;
}

/**
 * POST /conversations/{conversationId}/retag
 *
 * Manual re-tag trigger. Backend deletes TAGGED + CLEANED detail items,
 * NULLs the per-snippet speaker-resolution cache, and re-fires
 * tag_batch signals into ``SpeakerTaggingWorkflow``. The tagger →
 * scrubber → thread-analysis chain re-runs naturally via the existing
 * watermark / is_final_batch logic; no separate analysis trigger.
 *
 * Rate-limit: 1 manual retag / conv / 60s → 429 + Retry-After.
 * State guard: 422 unless ``recording_state === ENDED``.
 *
 * Returns ``{conversation_id, raw_detail_item_ids, snippets_invalidated,
 * detail_items_deleted, signals_fired}`` (HTTP 202).
 */
export async function triggerManualRetag(conversationId) {
  const response = await Api.post(
    `/conversations/${conversationId}/retag`,
    {},
  );
  return response.data;
}

/**
 * GET /threads/{threadId}/facts?category=<category>
 *
 * Returns the thread's facts for a category as an ascending-by-id array of
 * `ThreadFactOut` rows — `{id, category, value, context, supersedes_fact_id,
 * created_at}`. The list INCLUDES superseded rows; callers that want the
 * current surviving fact must filter them out (see
 * `getCurrentCoachingReport`).
 */
export async function getThreadFacts(threadId, { category } = {}) {
  const params = new URLSearchParams();
  if (category) params.set("category", category);
  const qs = params.toString();
  const response = await Api.get(
    `/threads/${threadId}/facts${qs ? `?${qs}` : ""}`
  );
  return response.data;
}

/**
 * Resolve the CURRENT analysis-v2 coaching report for a thread.
 *
 * The coaching output is emitted as a THREAD FACT under category
 * `report:coaching`. `GET /threads/{id}/facts` returns every row ascending by
 * id, INCLUDING superseded revisions. The surviving report is the one row
 * whose id is NOT referenced by any other row's `supersedes_fact_id`
 * (equivalently, the max-id row). Its `value` is a JSON string of the analysis
 * object — parse it and hand it to the report renderer selected by the UI.
 * Older facts store the renderer type only in the semicolon-delimited fact
 * context (for example, `analysisType=tonkin_sales_coaching`). Preserve any
 * explicit type in the report value, otherwise recover it from that context.
 *
 * Returns the parsed analysis object, or null if there is no report yet (or
 * the surviving row's value fails to parse).
 */
export async function getCurrentCoachingReport(threadId) {
  const facts = await getThreadFacts(threadId, { category: "report:coaching" });
  if (!Array.isArray(facts) || facts.length === 0) return null;
  // The surviving row is the one not superseded by any other row. Rows arrive
  // ascending by id, so the max-id row is the current one (a superseding row
  // always has a higher id than the row it supersedes).
  const superseded = new Set(
    facts.map((f) => f.supersedes_fact_id).filter((id) => id != null)
  );
  const current =
    facts.filter((f) => !superseded.has(f.id)).slice(-1)[0] ||
    facts[facts.length - 1];
  if (!current || current.value == null) return null;
  try {
    const report = typeof current.value === "string"
      ? JSON.parse(current.value)
      : current.value;
    if (!report || typeof report !== "object" || Array.isArray(report)) {
      return report;
    }
    const hasExplicitType = Boolean(
      report.analysis_type ||
      report.coaching_type ||
      report.metadata?.analysis_type ||
      report.metadata?.coaching_type
    );
    if (hasExplicitType) return report;

    const analysisType = String(current.context || "")
      .split(";")
      .map((entry) => entry.trim())
      .map((entry) => {
        const separator = entry.indexOf("=");
        if (separator < 0) return null;
        const key = entry
          .slice(0, separator)
          .trim()
          .toLowerCase()
          .replace(/[_-]/g, "");
        if (key !== "analysistype" && key !== "coachingtype") return null;
        return entry.slice(separator + 1).trim();
      })
      .find(Boolean);

    return analysisType ? { ...report, analysis_type: analysisType } : report;
  } catch (_e) {
    return null;
  }
}

/**
 * GET /conversations/{conversationId}
 *
 * Returns conversation metadata — most importantly `thread_id`, which the
 * live-transcript embed needs to subscribe to the per-thread SSE stream.
 */
export async function getConversation(conversationId) {
  const response = await Api.get(`/conversations/${conversationId}`);
  return response.data;
}

/**
 * GET /conversations/{conversationId}/details
 *
 * Eagerly-loaded conversation detail (snippet refs, transcripts, analysis).
 * See PRD §15.5 expanded transcript row.
 */
export async function getConversationDetails(conversationId) {
  const response = await Api.get(`/conversations/${conversationId}/details`);
  return response.data;
}

/**
 * GET /conversations/{conversationId}/detail_items?type=CLEANED_TRANSCRIPT
 *
 * Paginated list of cleaned-transcript detail items, used by the historic
 * transcript view (PRD §15.5).
 */
export async function getCleanedTranscriptItems(
  conversationId,
  { skip = 0, limit = 100 } = {}
) {
  return getConversationDetailItems(conversationId, {
    type: "CLEANED_TRANSCRIPT",
    skip,
    limit,
  });
}

/**
 * Build the absolute URL for `GET /conversations/{id}/audio` so the
 * browser-native `<audio>` element (driven by WaveSurfer) can stream the
 * assembled WAV directly with built-in Range support.
 *
 * Pairs with `getConversationAudioManifest` — the FE typically fetches the
 * manifest first to know total duration / per-batch offsets, then hands
 * this URL to WaveSurfer for actual playback.
 *
 * Returns a full URL (REACT_APP_BACKEND_URL + REACT_APP_API + path) so
 * media elements that don't share the axios baseURL can still load it.
 */
export function getConversationAudioUrl(conversationId) {
  const base = `${config.BACKEND_URL || ""}${config.API || ""}`;
  const path = `${base}/conversations/${conversationId}/audio`;
  // The browser's HTMLAudioElement (which WaveSurfer wraps via the
  // MediaElement backend) can't set custom Authorization headers — so
  // we attach the bearer token as `?access_token=` instead. Backend
  // policy middleware accepts the query fallback (same convention SSE
  // EventSource uses on /threads/{id}/events).
  const token = getCurrentToken();
  if (!token) return path;
  return `${path}?access_token=${encodeURIComponent(token)}`;
}

/**
 * GET /conversations/{conversationId}/audio/manifest
 *
 * Returns the byte-offset / duration manifest the conversation-audio
 * player uses to translate word-level `start_ms` timestamps into seek
 * targets without server round-trips. See `backend/app/conversation_audio.py`
 * for the locked wire shape.
 */
export async function getConversationAudioManifest(conversationId) {
  const response = await Api.get(
    `/conversations/${conversationId}/audio/manifest`
  );
  return response.data;
}

/**
 * GET /conversations/{conversationId}/audio/peaks?bins=N
 *
 * Returns pre-computed [min, max] PCM peaks across the assembled audio,
 * one pair per bin. The FE hands these to WaveSurfer's `peaks` option so
 * the waveform can render WITHOUT pre-loading the full WAV. Backend
 * caps `bins` at 4000 (see `app.conversation_audio.MAX_PEAKS_BINS`).
 *
 * Response shape:
 *   {
 *     conversation_id, duration_ms, bins, sample_rate,
 *     max_value: 32767,        // divide peaks by this for [-1, 1]
 *     peaks: [[min, max], ...]
 *   }
 */
export async function getConversationAudioPeaks(conversationId, bins = 1000) {
  const response = await Api.get(
    `/conversations/${conversationId}/audio/peaks`,
    { params: { bins } }
  );
  return response.data;
}

/**
 * GET /conversations/{conversationId}/detail_items?type=<type>
 *
 * Generic helper for any ConversationDetailItem.type — used by the desktop
 * `ConversationDetailPage` (Cleaned / Tagged / Raw transcript tabs).
 *
 * The `type` argument is one of `RAW_TRANSCRIPT`, `TAGGED_TRANSCRIPT`,
 * `CLEANED_TRANSCRIPT`, etc. (see `backend/app/models.py` for the full
 * enum).
 */
export async function getConversationDetailItems(
  conversationId,
  { type, skip = 0, limit = 100 } = {}
) {
  if (!type) throw new Error("getConversationDetailItems: type is required");
  const params = new URLSearchParams({
    type,
    skip: String(skip),
    limit: String(limit),
  });
  const response = await Api.get(
    `/conversations/${conversationId}/detail_items?${params.toString()}`
  );
  return response.data;
}

/**
 * POST /threads/bulk-import — start a BulkImportWorkflow on Temporal.
 *
 * Two source shapes are supported by the backend, mirroring the
 * BulkImportThreadRequest pydantic model. Callers pass EITHER:
 *
 *   - `testAudioFileId` (preferred): the new TestAudioFile flow. The
 *     backend looks up the row, verifies READY + org-scope, forwards its
 *     normalised WAV s3 location to Temporal, and bumps last_used_at.
 *
 *   - `sourceAudioS3Bucket + sourceAudioS3Key` (DEPRECATED): the original
 *     "Bulk Create Thread" shape. Still accepted for backwards-compat with
 *     any out-of-tree caller. The in-app UI uses the preferred shape only.
 *
 * Returns `{thread_id, conversation_id, workflow_id}`.
 */
export async function bulkImportThread({
  title,
  durationMs,
  testAudioFileId,
  sourceAudioS3Bucket,
  sourceAudioS3Key,
}) {
  const body = { title, duration_ms: durationMs };
  if (testAudioFileId != null) {
    body.test_audio_file_id = testAudioFileId;
  } else {
    body.source_audio_s3_bucket = sourceAudioS3Bucket;
    body.source_audio_s3_key = sourceAudioS3Key;
  }
  const response = await Api.post("/threads/bulk-import", body);
  return response.data;
}

/**
 * Build the SSE endpoint URL for a given thread, including the bearer
 * token as a query parameter (EventSource doesn't support custom headers).
 *
 * Backend `/threads/{id}/sse` accepts the token either via Authorization
 * header (XHR / fetch) or via `access_token` query param (EventSource
 * fallback) — same convention as upstream Keycloak streams. (The SSE stream
 * moved off `/threads/{id}/events` so the durable thread_events cursor log
 * could own that path — deploy in lockstep with the backend change.)
 */
export function buildThreadEventsUrl(threadId, token) {
  const base = config.BACKEND_URL || "";
  const apiPrefix = config.API || "";
  const path = `${base}${apiPrefix}/threads/${threadId}/sse`;
  if (!token) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}access_token=${encodeURIComponent(token)}`;
}
